(ns io.daskul.webapp.aggregates.events
  "Generic CRUD events for aggregates.

   Provides parameterized re-frame events for standard operations.
   Configure via `configure!` function.

   ## Available Events

   CRUD:
   - ::list - Fetch all records
   - ::get - Fetch single record by ID
   - ::create - Create new record
   - ::patch - Update existing record
   - ::delete - Delete record by ID

   Schema:
   - ::fetch-schema - Fetch form schema for operation

   Lookups:
   - ::lookup - Fetch options for relationship field
   - ::clear-lookup-options - Clear lookup options

   FSM:
   - ::transition - Execute FSM state transition

   Embedded Relationships:
   - ::list-embedded - List embedded items
   - ::add-embedded - Add embedded item

   Child Aggregates:
   - ::list-children - List child items
   - ::create-child - Create child item
   - ::patch-child - Update child item
   - ::delete-child - Delete child item
   - ::transition-child - Transition child FSM state

   Counts:
   - ::counts - Fetch grouped counts for segmented filter badges"
  (:require
   [clojure.string :as str]
   [re-frame.core :as rf]
   [ajax.core :as ajax]
   [io.daskul.webapp.api.core :as api.core]
   [io.daskul.webapp.aggregates.db :as db]))

;; ============================================================================
;; Configuration
;; ============================================================================

;; Config + HTTP policy live in io.daskul.webapp.api.core (ajax-free) so the
;; pure request-base is unit-testable. We alias the shared atom + re-export the
;; public surface here, keeping every existing agg.events/* caller working.
(def ^:private config api.core/config)

(def configure! api.core/configure!)
(def get-api-base api.core/get-api-base)
(def get-auth-header api.core/get-auth-header)

(defn- log-error [msg data]
  ((:log-error @config) msg data))

(defn get-resource-name
  "Get REST resource name for aggregate keyword.
   Uses configured mapping or defaults to (name agg-key).
   Throws if not registered and no default is sensible."
  [agg-key]
  (if-let [resource (get-in @config [:resource-names agg-key])]
    resource
    (let [available (keys (:resource-names @config))]
      (if (seq available)
        (do (log-error "Aggregate not registered" {:aggregate agg-key :available available})
            (throw (js/Error. (str "Aggregate not registered: " agg-key
                                   "\nAvailable: " (pr-str available)))))
        ;; No mappings configured yet (app startup) - fallback to name
        (name agg-key)))))

(defn- get-label
  "Get human-readable label for an aggregate. Falls back to (name agg-key)."
  [db agg-key]
  (or (when-let [f (:get-label @config)] (f db agg-key))
      (name agg-key)))

(defn- success-notification
  "Build success notification effect map entry, or nil if not configured."
  [title message]
  (when-let [f (:notify-success @config)]
    (f title message)))

(defn- error-notification
  "Build error notification effect map entry, or nil if not configured."
  [title message]
  (when-let [f (:notify-error @config)]
    (f title message)))

(defn- describe-error
  "Build a descriptive error message from parsed error.
   Includes field error details when the base message is generic."
  [{:keys [message field-errors]}]
  (if (seq field-errors)
    (let [field-msgs (map (fn [[k v]]
                            (str (name k) ": " v))
                          field-errors)]
      (str/join ", " field-msgs))
    (or message "An error occurred")))

;; Re-export the shared 401/error policy (defined in api.core) so ::http-failure
;; below and the imperative api.http/request! path use ONE implementation.
(def handle-http-error api.core/handle-http-error)

;; ============================================================================
;; HTTP Interceptors (401 handling, success forwarding)
;; ============================================================================

(rf/reg-event-fx ::http-success
  (fn [_ [_ custom-event response]]
    {:dispatch (conj custom-event response)}))

(rf/reg-event-fx ::http-failure
  (fn [_ [_ custom-event {:keys [status] :as error}]]
    (if-let [effect (handle-http-error error)]
      effect
      {:dispatch (conj custom-event error)})))

;; ============================================================================
;; API Request Helper
;; ============================================================================

(defn base-opts
  "Shared request bits for every HTTP idiom: composed URI + auth headers (from
   the ajax-free api.core) PLUS the ajax JSON request/response formats. Single
   source of truth used by build-request (re-frame :http-xhrio) AND the
   imperative api.http/request!. The 401/error policy lives in handle-http-error,
   wired by each caller into its failure path."
  [path]
  (assoc (api.core/base-opts path)
         :format (ajax/json-request-format)
         :response-format (ajax/json-response-format {:keywords? true})))

(defn- api-request
  "Build HTTP request map for aggregate resource.
   Wraps on-success/on-failure through HTTP interceptors for 401 handling."
  [{:keys [method resource path on-success on-failure params]}]
  (assoc (base-opts (str "/" resource path))
         :method method
         :params params
         :on-success [::http-success on-success]
         :on-failure [::http-failure on-failure]))

(defn build-request
  "Public API request builder for custom aggregate endpoints.
   Returns an :http-xhrio map with auth headers, base URL, format, and 401 handling.

   Options:
   - :method - HTTP method (:get, :post, :patch, :delete)
   - :path   - URL path relative to API base (e.g. \"/conversations/123/messages\")
   - :params - Request params map
   - :on-success - Re-frame event vector for success handler
   - :on-failure - Re-frame event vector for failure handler"
  [{:keys [method path on-success on-failure params]}]
  (assoc (base-opts path)
         :method method
         :params params
         :on-success [::http-success on-success]
         :on-failure [::http-failure on-failure]))

;; ============================================================================
;; Normalization Helpers
;; ============================================================================

(defn- index-by-id [coll]
  (into {} (map (juxt :id identity) coll)))

(defn- normalize-response [items]
  {:entities (index-by-id items)
   :ids (mapv :id items)})

;; ============================================================================
;; Error Parsing
;; ============================================================================

(defn- parse-error-response
  "Parse API error response into structured format."
  [error]
  (let [body (:response error)
        error-type (:error body)
        message (:message body)
        details (:details body)]
    (cond
      (= error-type "validation-failed")
      (let [;; Handle both formats:
            ;; - Reitit coercion: {:field-errors {:name ["..."]}}
            ;; - Aggregate validation: {:name ["..."]} (humanized directly)
            raw-errors (or (:field-errors details) details)]
        {:message message
         :field-errors (into {}
                         (keep (fn [[k v]]
                                 (when (keyword? k)
                                   [k (if (sequential? v) (first v) v)])))
                         raw-errors)})

      (= error-type "business-rule-violation")
      (let [;; Same format as validation: {:field-key ["msg"]}
            field-errors (when (map? details)
                           (into {}
                             (keep (fn [[k v]]
                                     (when (keyword? k)
                                       [k (if (sequential? v) (first v) v)])))
                             details))]
        {:message message
         :field-errors (or field-errors {})})

      :else
      {:message (or message (:status-text error) "An error occurred")
       :field-errors {}})))

;; ============================================================================
;; Generic CRUD Events
;; ============================================================================

;; List
;; opts: {:silent true} to skip setting loading (useful for polling)
(rf/reg-event-fx ::list
  (fn [{:keys [db]} [_ agg-key params opts]]
    (cond-> {:http-xhrio (api-request {:method :get
                                       :resource (get-resource-name agg-key)
                                       :path ""
                                       :on-success [::list-success agg-key]
                                       :on-failure [::list-failure agg-key]
                                       :params params})}
      (not (:silent opts))
      (assoc :db (assoc-in db (db/loading-path agg-key) true)))))

(rf/reg-event-db ::list-success
  (fn [db [_ agg-key response]]
    (let [items (:items response)
          {:keys [entities ids]} (normalize-response items)]
      (-> db
          (update-in (db/entities-path agg-key) merge entities)
          (assoc-in (db/list-ids-path agg-key) ids)
          (assoc-in (db/pagination-path agg-key) (:pagination response))
          (assoc-in (db/loading-path agg-key) false)
          (assoc-in (db/error-path agg-key) nil)))))

(rf/reg-event-fx ::list-failure
  (fn [{:keys [db]} [_ agg-key error]]
    (log-error "Failed to list" {:aggregate agg-key :error error})
    (let [label (get-label db agg-key)
          parsed (parse-error-response error)]
      (cond-> {:db (-> db
                       (assoc-in (db/error-path agg-key) parsed)
                       (assoc-in (db/loading-path agg-key) false))}
        true (merge (error-notification (str "Failed to load " label) (describe-error parsed)))))))

;; Get
(rf/reg-event-fx ::get
  (fn [{:keys [db]} [_ agg-key id]]
    {:db (assoc-in db (db/loading-path agg-key) true)
     :http-xhrio (api-request {:method :get
                               :resource (get-resource-name agg-key)
                               :path (str "/" id)
                               :on-success [::get-success agg-key]
                               :on-failure [::get-failure agg-key]})}))

(rf/reg-event-db ::get-success
  (fn [db [_ agg-key response]]
    (let [id (:id response)]
      (-> db
          (assoc-in (db/entity-path agg-key id) response)
          (assoc-in (db/current-id-path agg-key) id)
          (assoc-in (db/loading-path agg-key) false)
          (assoc-in (db/error-path agg-key) nil)))))

(rf/reg-event-fx ::get-failure
  (fn [{:keys [db]} [_ agg-key error]]
    (log-error "Failed to get" {:aggregate agg-key :error error})
    (let [label (get-label db agg-key)
          parsed (parse-error-response error)]
      (cond-> {:db (-> db
                       (assoc-in (db/error-path agg-key) parsed)
                       (assoc-in (db/loading-path agg-key) false))}
        true (merge (error-notification (str "Failed to load " label) (describe-error parsed)))))))

;; Create
(rf/reg-event-fx ::create
  (fn [{:keys [db]} [_ agg-key params]]
    {:db (assoc-in db (db/creating-path agg-key) true)
     :http-xhrio (api-request {:method :post
                               :resource (get-resource-name agg-key)
                               :path ""
                               :on-success [::create-success agg-key]
                               :on-failure [::create-failure agg-key]
                               :params params})}))

(rf/reg-event-fx ::create-success
  (fn [{:keys [db]} [_ agg-key response]]
    (let [id (:id response)
          label (get-label db agg-key)]
      (cond-> {:db (-> db
                       (assoc-in (db/entity-path agg-key id) response)
                       (update-in (db/list-ids-path agg-key) (fnil conj []) id)
                       (assoc-in (db/creating-path agg-key) false)
                       (assoc-in (db/error-path agg-key) nil))
               :dispatch-later [{:ms 100 :dispatch [:webapp/on-create-success agg-key response]}]}
        true (merge (success-notification "Created" (str label " created successfully")))))))

(rf/reg-event-fx ::create-failure
  (fn [{:keys [db]} [_ agg-key error]]
    (log-error "Failed to create" {:aggregate agg-key :error error})
    (let [label (get-label db agg-key)
          parsed (parse-error-response error)]
      (cond-> {:db (-> db
                       (assoc-in (db/error-path agg-key) parsed)
                       (assoc-in (db/creating-path agg-key) false))}
        ;; Only show toast for non-validation errors (field errors shown inline)
        (empty? (:field-errors parsed))
        (merge (error-notification (str "Failed to create " label) (describe-error parsed)))))))

;; Clear field error
(rf/reg-event-db ::clear-field-error
  (fn [db [_ agg-key field-key]]
    (update-in db (conj (db/error-path agg-key) :field-errors) dissoc field-key)))

;; Clear transition field error (inline errors on transition forms).
;; When all field errors are resolved, also clear the form-level :message
;; so the stale "Transition params validation failed" alert disappears.
(rf/reg-event-db ::clear-transition-field-error
  (fn [db [_ agg-key field-key]]
    (let [path (db/transition-errors-path agg-key)
          db' (update-in db (conj path :field-errors) dissoc field-key)
          remaining (get-in db' (conj path :field-errors))]
      (if (empty? remaining)
        (assoc-in db' path nil)
        db'))))

;; Patch
(rf/reg-event-fx ::patch
  (fn [{:keys [db]} [_ agg-key id params]]
    {:db (assoc-in db (db/updating-path agg-key) true)
     :http-xhrio (api-request {:method :patch
                               :resource (get-resource-name agg-key)
                               :path (str "/" id)
                               :on-success [::patch-success agg-key]
                               :on-failure [::patch-failure agg-key]
                               :params params})}))

(rf/reg-event-fx ::patch-success
  (fn [{:keys [db]} [_ agg-key response]]
    (let [id (:id response)
          label (get-label db agg-key)]
      ;; Merge response into existing record so related/joined data from GET
      ;; (e.g., student name, program name) is preserved — PATCH only returns flat fields.
      (cond-> {:db (-> db
                       (update-in (db/entity-path agg-key id) merge response)
                       (assoc-in (db/updating-path agg-key) false)
                       (assoc-in (db/error-path agg-key) nil))
               :dispatch-later [{:ms 100 :dispatch [:webapp/on-patch-success agg-key response]}]}
        true (merge (success-notification "Updated" (str label " updated successfully")))))))

(rf/reg-event-fx ::patch-failure
  (fn [{:keys [db]} [_ agg-key error]]
    (log-error "Failed to patch" {:aggregate agg-key :error error})
    (let [label (get-label db agg-key)
          parsed (parse-error-response error)]
      (cond-> {:db (-> db
                       (assoc-in (db/error-path agg-key) parsed)
                       (assoc-in (db/updating-path agg-key) false))}
        (empty? (:field-errors parsed))
        (merge (error-notification (str "Failed to update " label) (describe-error parsed)))))))

;; Delete
(rf/reg-event-fx ::delete
  (fn [{:keys [db]} [_ agg-key id]]
    {:db (assoc-in db (db/deleting-path agg-key) true)
     :http-xhrio (api-request {:method :delete
                               :resource (get-resource-name agg-key)
                               :path (str "/" id)
                               :on-success [::delete-success agg-key id]
                               :on-failure [::delete-failure agg-key]})}))

(rf/reg-event-fx ::delete-success
  (fn [{:keys [db]} [_ agg-key id _response]]
    (let [label (get-label db agg-key)]
      (cond-> {:db (-> db
                       (update-in (db/entities-path agg-key) dissoc id)
                       (update-in (db/list-ids-path agg-key) (fn [ids] (vec (remove #(= % id) ids))))
                       (assoc-in (db/deleting-path agg-key) false)
                       (assoc-in (db/error-path agg-key) nil))
               :dispatch-later [{:ms 100 :dispatch [:webapp/on-delete-success agg-key id]}]}
        true (merge (success-notification "Deleted" (str label " deleted successfully")))))))

(rf/reg-event-fx ::delete-failure
  (fn [{:keys [db]} [_ agg-key error]]
    (log-error "Failed to delete" {:aggregate agg-key :error error})
    (let [label (get-label db agg-key)
          parsed (parse-error-response error)]
      (cond-> {:db (-> db
                       (assoc-in (db/error-path agg-key) parsed)
                       (assoc-in (db/deleting-path agg-key) false))}
        true (merge (error-notification (str "Failed to delete " label) (describe-error parsed)))))))

;; ============================================================================
;; Schema - Fetch form schema from backend
;; ============================================================================

(rf/reg-event-fx ::fetch-schema
  (fn [{:keys [db]} [_ agg-key operation]]
    (let [resource (get-resource-name agg-key)]
      {:db (assoc-in db (db/schema-loading-path agg-key) true)
       :http-xhrio {:method :get
                    :uri (str (:api-base @config) "/_schema/" resource "/" (name operation))
                    :headers ((:auth-header @config))
                    :response-format (ajax/json-response-format {:keywords? true})
                    :on-success [::http-success [::fetch-schema-success agg-key operation]]
                    :on-failure [::http-failure [::fetch-schema-failure agg-key]]}})))

(defn- resolve-lookup-url
  "Resolve a :lookup-url value. Strings pass through.
   Maps are resolved using aggregate base-paths from _config:
     {:aggregate :enrollment-type} → \"/enrollment-types/lookup\"
     {:aggregate :org-unit :params {:type \"branch\"}} → \"/org-units/lookup?type=branch\""
  [lookup-url aggregates]
  (if (string? lookup-url)
    lookup-url
    (when (map? lookup-url)
      (let [agg-key (keyword (:aggregate lookup-url))
            base-path (:base-path (get aggregates agg-key))
            params (:params lookup-url)]
        (when base-path
          (if (seq params)
            (let [qs (->> params
                          (map (fn [[k v]] (str (name k) "=" v)))
                          (str/join "&"))]
              (str base-path "/lookup?" qs))
            (str base-path "/lookup")))))))

(defn normalize-schema
  "Normalize schema from API - convert field names to keywords.
   Also converts :source-path elements (for cross-aggregate fields)
   and resolves map-form :lookup-url in field UI specs."
  [schema aggregates]
  (update schema :fields
          (fn [fields]
            (mapv (fn [field]
                    (cond-> (update field :name keyword)
                      (:source-path field)
                      (update :source-path #(mapv keyword %))
                      (get-in field [:ui :lookup-url])
                      (update-in [:ui :lookup-url] resolve-lookup-url aggregates)))
                  fields))))

(rf/reg-event-db ::fetch-schema-success
  (fn [db [_ agg-key operation response]]
    (let [aggregates (get-in db [:config :data :aggregates])]
      (-> db
          (assoc-in (db/schema-path agg-key operation) (normalize-schema response aggregates))
          (assoc-in (db/schema-loading-path agg-key) false)))))

(rf/reg-event-fx ::fetch-schema-failure
  (fn [{:keys [db]} [_ agg-key error]]
    (log-error "Failed to fetch schema" {:aggregate agg-key :error error})
    (let [label (get-label db agg-key)
          parsed (parse-error-response error)]
      (cond-> {:db (-> db
                       (assoc-in (db/ui-path agg-key :schema-error) parsed)
                       (assoc-in (db/schema-loading-path agg-key) false))}
        true (merge (error-notification (str "Failed to load " label " form") (describe-error parsed)))))))

;; ============================================================================
;; Lookup - Fetch options for relationship fields
;; ============================================================================

(rf/reg-event-fx ::lookup
  (fn [{:keys [db]} [_ agg-key lookup-url field-key]]
    {:db (assoc-in db (db/lookup-path agg-key field-key :loading?) true)
     :http-xhrio {:method :get
                  :uri (str (:api-base @config) lookup-url)
                  :headers ((:auth-header @config))
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success [::http-success [::lookup-success agg-key field-key]]
                  :on-failure [::http-failure [::lookup-failure agg-key field-key]]}}))

(rf/reg-event-db ::lookup-success
  (fn [db [_ agg-key field-key response]]
    (-> db
        (assoc-in (db/lookup-path agg-key field-key :items) (:items response))
        (assoc-in (db/lookup-path agg-key field-key :loading?) false))))

(rf/reg-event-fx ::lookup-failure
  (fn [{:keys [db]} [_ agg-key field-key error]]
    (log-error "Failed to fetch lookup" {:field field-key :error error})
    (let [parsed (parse-error-response error)]
      (cond-> {:db (-> db
                       (assoc-in (db/lookup-path agg-key field-key :error) parsed)
                       (assoc-in (db/lookup-path agg-key field-key :loading?) false))}
        true (merge (error-notification (str "Failed to load " (name field-key) " options") (describe-error parsed)))))))

(rf/reg-event-db ::clear-lookup-options
  (fn [db [_ agg-key field-key]]
    (assoc-in db (db/lookup-path agg-key field-key :items) [])))

;; ============================================================================
;; FSM Transitions
;; ============================================================================

(rf/reg-event-fx ::transition
  ;; opts (optional) — {:on-success (fn [record]) :on-failure (fn [errors])}:
  ;; caller callbacks fired after the transition settles, for surface refreshes
  ;; the framework can't infer (a parent chip, a sibling list). on-success
  ;; receives the transitioned record, on-failure the parsed errors. Threaded
  ;; to the success/failure handlers so they fire at exactly the right time.
  (fn [{:keys [db]} [_ agg-key id event params opts]]
    {:db (-> db
             (assoc-in (db/transitioning-path agg-key) true)
             (assoc-in (db/transition-event-path agg-key) event)
             (assoc-in (db/transition-errors-path agg-key) nil))
     :http-xhrio (api-request {:method :post
                               :resource (get-resource-name agg-key)
                               :path (str "/" id "/transition")
                               :on-success [::transition-success agg-key id opts]
                               :on-failure [::transition-failure agg-key opts]
                               :params (cond-> {:event event}
                                         (seq params) (assoc :params params))})}))

(rf/reg-event-fx ::transition-success
  (fn [{:keys [db]} [_ agg-key id opts response]]
    (when-let [f (:on-success opts)] (f response))
    ;; A :transition returns the updated record; a :dispatch (F-053) returns the
    ;; handler's value verbatim (no :id), which is NOT the record. Only merge a
    ;; real record into the store — for a dispatch, just refetch by the known id
    ;; so the title/display-sections/enrichment reload instead of being wiped.
    (let [record? (some? (:id response))
          label (get-label db agg-key)
          children (get-in db [:config :data :aggregates agg-key :children])
          child-dispatches (mapv (fn [{:keys [aggregate fk-field]}]
                                  [::list-children agg-key id aggregate fk-field])
                                children)]
      (cond-> {:db (-> db
                       (cond-> record? (assoc-in (db/entity-path agg-key id) response))
                       (assoc-in (db/transitioning-path agg-key) false)
                       (assoc-in (db/transition-event-path agg-key) nil)
                       (assoc-in (db/transition-errors-path agg-key) nil)
                       (assoc-in (db/error-path agg-key) nil))
               ;; Re-fetch parent + any child tabs
               :dispatch-n (into [[::get agg-key id]] child-dispatches)}
        true (merge (success-notification "Updated" (str label " updated successfully")))))))

(rf/reg-event-fx ::transition-failure
  (fn [{:keys [db]} [_ agg-key opts error]]
    (log-error "Failed to transition" {:aggregate agg-key :error error})
    (let [label (get-label db agg-key)
          parsed (parse-error-response error)
          has-field-errors? (seq (:field-errors parsed))]
      (when-let [f (:on-failure opts)] (f parsed))
      (cond-> {:db (-> db
                       (assoc-in (db/transition-errors-path agg-key) parsed)
                       (assoc-in (db/transitioning-path agg-key) false))}
        ;; Only show toast when no field errors (field errors show inline on form)
        (not has-field-errors?)
        (merge (error-notification (str "Failed to transition " label) (describe-error parsed)))))))

;; ============================================================================
;; Embedded Relationships
;; ============================================================================

(rf/reg-event-fx ::list-embedded
  (fn [{:keys [db]} [_ agg-key parent-id rel-name]]
    {:db (assoc-in db (db/embedded-path agg-key parent-id rel-name :loading?) true)
     :http-xhrio (api-request {:method :get
                               :resource (get-resource-name agg-key)
                               :path (str "/" parent-id "/" (name rel-name))
                               :on-success [::list-embedded-success agg-key parent-id rel-name]
                               :on-failure [::list-embedded-failure agg-key parent-id rel-name]})}))

(rf/reg-event-db ::list-embedded-success
  (fn [db [_ agg-key parent-id rel-name response]]
    (let [items (:items response)]
      (-> db
          (assoc-in (db/embedded-path agg-key parent-id rel-name :items) items)
          (assoc-in (db/embedded-path agg-key parent-id rel-name :loading?) false)
          (assoc-in (db/embedded-path agg-key parent-id rel-name :error) nil)))))

(rf/reg-event-fx ::list-embedded-failure
  (fn [{:keys [db]} [_ agg-key parent-id rel-name error]]
    (log-error "Failed to list embedded" {:aggregate agg-key :rel rel-name :error error})
    (let [parsed (parse-error-response error)]
      (cond-> {:db (-> db
                       (assoc-in (db/embedded-path agg-key parent-id rel-name :error) parsed)
                       (assoc-in (db/embedded-path agg-key parent-id rel-name :loading?) false))}
        true (merge (error-notification (str "Failed to load " (name rel-name)) (describe-error parsed)))))))

(rf/reg-event-fx ::add-embedded
  (fn [{:keys [db]} [_ agg-key parent-id rel-name data]]
    {:db (assoc-in db (db/embedded-path agg-key parent-id rel-name :saving?) true)
     :http-xhrio (api-request {:method :post
                               :resource (get-resource-name agg-key)
                               :path (str "/" parent-id "/" (name rel-name))
                               :on-success [::add-embedded-success agg-key parent-id rel-name]
                               :on-failure [::add-embedded-failure agg-key parent-id rel-name]
                               :params data})}))

(rf/reg-event-fx ::add-embedded-success
  (fn [{:keys [db]} [_ agg-key parent-id rel-name _response]]
    {:db (-> db
             (assoc-in (db/embedded-path agg-key parent-id rel-name :saving?) false)
             (assoc-in (db/embedded-path agg-key parent-id rel-name :form-error) nil)
             (assoc-in (db/embedded-path agg-key parent-id rel-name :save-success?) true))
     :dispatch [::list-embedded agg-key parent-id rel-name]}))

(rf/reg-event-fx ::add-embedded-failure
  (fn [{:keys [db]} [_ agg-key parent-id rel-name error]]
    (log-error "Failed to add embedded" {:aggregate agg-key :rel rel-name :error error})
    (let [parsed (parse-error-response error)]
      (cond-> {:db (-> db
                       (assoc-in (db/embedded-path agg-key parent-id rel-name :form-error) parsed)
                       (assoc-in (db/embedded-path agg-key parent-id rel-name :saving?) false))}
        true (merge (error-notification (str "Failed to save " (name rel-name)) (describe-error parsed)))))))

(rf/reg-event-db ::clear-embedded-save-success
  (fn [db [_ agg-key parent-id rel-name]]
    (assoc-in db (db/embedded-path agg-key parent-id rel-name :save-success?) false)))

;; ============================================================================
;; Child Aggregates
;; ============================================================================

(rf/reg-event-fx ::list-children
  (fn [{:keys [db]} [_ parent-agg-key parent-id child-agg-key fk-field]]
    (let [resource (get-resource-name child-agg-key)
          filter-params {fk-field parent-id}]
      {:db (assoc-in db (db/child-path parent-agg-key parent-id child-agg-key :loading?) true)
       :http-xhrio (api-request {:method :get
                                 :resource resource
                                 :path ""
                                 :on-success [::list-children-success parent-agg-key parent-id child-agg-key]
                                 :on-failure [::list-children-failure parent-agg-key parent-id child-agg-key]
                                 :params filter-params})})))

(rf/reg-event-db ::list-children-success
  (fn [db [_ parent-agg-key parent-id child-agg-key response]]
    (let [items (:items response)]
      (-> db
          (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :items) items)
          (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :loading?) false)
          (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :error) nil)))))

(rf/reg-event-fx ::list-children-failure
  (fn [{:keys [db]} [_ parent-agg-key parent-id child-agg-key error]]
    (log-error "Failed to list children" {:parent parent-agg-key :child child-agg-key :error error})
    (let [label (get-label db child-agg-key)
          parsed (parse-error-response error)]
      (cond-> {:db (-> db
                       (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :error) parsed)
                       (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :loading?) false))}
        true (merge (error-notification (str "Failed to load " label) (describe-error parsed)))))))

(rf/reg-event-fx ::create-child
  (fn [{:keys [db]} [_ parent-agg-key parent-id child-agg-key fk-field data]]
    (let [resource (get-resource-name child-agg-key)
          full-data (assoc data fk-field parent-id)]
      {:db (assoc-in db (db/child-path parent-agg-key parent-id child-agg-key :saving?) true)
       :http-xhrio (api-request {:method :post
                                 :resource resource
                                 :path ""
                                 :on-success [::create-child-success parent-agg-key parent-id child-agg-key fk-field]
                                 :on-failure [::create-child-failure parent-agg-key parent-id child-agg-key]
                                 :params full-data})})))

(rf/reg-event-fx ::create-child-success
  (fn [{:keys [db]} [_ parent-agg-key parent-id child-agg-key fk-field _response]]
    {:db (-> db
             (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :saving?) false)
             (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :form-error) nil)
             (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :save-success?) true))
     :dispatch-n [[::list-children parent-agg-key parent-id child-agg-key fk-field]
                  [::get parent-agg-key parent-id]]}))

(rf/reg-event-fx ::create-child-failure
  (fn [{:keys [db]} [_ parent-agg-key parent-id child-agg-key error]]
    (log-error "Failed to create child" {:parent parent-agg-key :child child-agg-key :error error})
    (let [label (get-label db child-agg-key)
          parsed (parse-error-response error)]
      (cond-> {:db (-> db
                       (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :form-error) parsed)
                       (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :saving?) false))}
        ;; Only show toast for non-validation errors (field errors shown inline)
        (empty? (:field-errors parsed))
        (merge (error-notification (str "Failed to create " label) (describe-error parsed)))))))

(rf/reg-event-fx ::patch-child
  (fn [{:keys [db]} [_ parent-agg-key parent-id child-agg-key fk-field child-id data]]
    {:db (assoc-in db (db/child-path parent-agg-key parent-id child-agg-key :saving?) true)
     :http-xhrio (api-request {:method :patch
                               :resource (get-resource-name child-agg-key)
                               :path (str "/" child-id)
                               :on-success [::patch-child-success parent-agg-key parent-id child-agg-key fk-field]
                               :on-failure [::patch-child-failure parent-agg-key parent-id child-agg-key]
                               :params data})}))

(rf/reg-event-fx ::patch-child-success
  (fn [{:keys [db]} [_ parent-agg-key parent-id child-agg-key fk-field _response]]
    {:db (-> db
             (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :saving?) false)
             (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :form-error) nil)
             (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :save-success?) true))
     :dispatch-n [[::list-children parent-agg-key parent-id child-agg-key fk-field]
                  [::get parent-agg-key parent-id]]}))

(rf/reg-event-fx ::patch-child-failure
  (fn [{:keys [db]} [_ parent-agg-key parent-id child-agg-key error]]
    (log-error "Failed to patch child" {:parent parent-agg-key :child child-agg-key :error error})
    (let [label (get-label db child-agg-key)
          parsed (parse-error-response error)]
      (cond-> {:db (-> db
                       (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :form-error) parsed)
                       (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :saving?) false))}
        ;; Only show toast for non-validation errors (field errors shown inline)
        (empty? (:field-errors parsed))
        (merge (error-notification (str "Failed to update " label) (describe-error parsed)))))))

(rf/reg-event-fx ::delete-child
  (fn [{:keys [db]} [_ parent-agg-key parent-id child-agg-key fk-field child-id]]
    {:db (assoc-in db (db/child-path parent-agg-key parent-id child-agg-key :deleting?) true)
     :http-xhrio (api-request {:method :delete
                               :resource (get-resource-name child-agg-key)
                               :path (str "/" child-id)
                               :on-success [::delete-child-success parent-agg-key parent-id child-agg-key fk-field]
                               :on-failure [::delete-child-failure parent-agg-key parent-id child-agg-key]})}))

(rf/reg-event-fx ::delete-child-success
  (fn [{:keys [db]} [_ parent-agg-key parent-id child-agg-key fk-field _response]]
    {:db (-> db
             (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :deleting?) false)
             (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :delete-error) nil))
     :dispatch [::list-children parent-agg-key parent-id child-agg-key fk-field]}))

(rf/reg-event-fx ::delete-child-failure
  (fn [{:keys [db]} [_ parent-agg-key parent-id child-agg-key error]]
    (log-error "Failed to delete child" {:parent parent-agg-key :child child-agg-key :error error})
    (let [label (get-label db child-agg-key)
          parsed (parse-error-response error)]
      (cond-> {:db (-> db
                       (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :delete-error) parsed)
                       (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :deleting?) false))}
        true (merge (error-notification (str "Failed to delete " label) (describe-error parsed)))))))

(rf/reg-event-fx ::transition-child
  (fn [{:keys [db]} [_ parent-agg-key parent-id child-agg-key fk-field child-id event params]]
    {:db (-> db
             (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :transitioning?) true)
             (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :transition-event) event))
     :http-xhrio (api-request {:method :post
                               :resource (get-resource-name child-agg-key)
                               :path (str "/" child-id "/transition")
                               :on-success [::transition-child-success parent-agg-key parent-id child-agg-key fk-field]
                               :on-failure [::transition-child-failure parent-agg-key parent-id child-agg-key]
                               :params (cond-> {:event event}
                                         (seq params) (assoc :params params))})}))

(rf/reg-event-fx ::transition-child-success
  (fn [{:keys [db]} [_ parent-agg-key parent-id child-agg-key fk-field _response]]
    {:db (-> db
             (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :transitioning?) false)
             (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :transition-event) nil))
     :dispatch-n [[::list-children parent-agg-key parent-id child-agg-key fk-field]
                  [::get parent-agg-key parent-id]]}))

(rf/reg-event-fx ::transition-child-failure
  (fn [{:keys [db]} [_ parent-agg-key parent-id child-agg-key error]]
    (log-error "Failed to transition child" {:parent parent-agg-key :child child-agg-key :error error})
    (let [label (get-label db child-agg-key)
          parsed (parse-error-response error)]
      (cond-> {:db (-> db
                       (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :transition-error) parsed)
                       (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :transitioning?) false)
                       (assoc-in (db/child-path parent-agg-key parent-id child-agg-key :transition-event) nil))}
        true (merge (error-notification (str "Failed to transition " label) (describe-error parsed)))))))

(rf/reg-event-db ::clear-child-save-success
  (fn [db [_ parent-agg-key parent-id child-agg-key]]
    (assoc-in db (db/child-path parent-agg-key parent-id child-agg-key :save-success?) false)))

(rf/reg-event-db ::clear-child-form-error
  (fn [db [_ parent-agg-key parent-id child-agg-key]]
    (assoc-in db (db/child-path parent-agg-key parent-id child-agg-key :form-error) nil)))

(rf/reg-event-db ::clear-child-field-error
  (fn [db [_ parent-agg-key parent-id child-agg-key field-key]]
    (update-in db (db/child-path parent-agg-key parent-id child-agg-key :form-error :field-errors) dissoc field-key)))

;; ============================================================================
;; Counts - Fetch grouped counts for segmented filters
;; ============================================================================

(rf/reg-event-fx ::counts
  (fn [{:keys [db]} [_ agg-key group-by-field params]]
    {:http-xhrio (api-request {:method :get
                               :resource (get-resource-name agg-key)
                               :path "/counts"
                               :on-success [::counts-success agg-key group-by-field]
                               :on-failure [::counts-failure agg-key group-by-field]
                               :params (assoc params :group-by (name group-by-field))})}))

(rf/reg-event-db ::counts-success
  (fn [db [_ agg-key group-by-field response]]
    (let [counts (get response group-by-field)]
      (assoc-in db (db/counts-path agg-key group-by-field) counts))))

(rf/reg-event-fx ::counts-failure
  (fn [{:keys [db]} [_ agg-key group-by-field error]]
    (log-error "Failed to fetch counts" {:aggregate agg-key :field group-by-field :error error})
    {:db db}))
